"use client";

import { useLingui } from "@lingui/react/macro";
import { LicensePlate } from "@offstreetparking/design-system/dataDisplay/LicensePlate";
import { Button } from "@offstreetparking/design-system/forms";
import { Heading } from "@offstreetparking/design-system/typography";
import { Text } from "@offstreetparking/design-system/typography";
import { useId, useState } from "react";
import { useKiosk } from "../../../providers/KioskProvider";
import { EditPermitVehiclesModal } from "./EditPermitVehiclesModal";

type VehicleManagementSectionProps = {
	vehicles: {
		id: number;
		plate: string;
		state: string;
	}[];
	permitStatus: string;
	publicAccessToken?: string | null;
	parkerId?: number;
	isEditPermitDisabled: boolean;
	publicIdentifier: string;
	companyCountry?: string;
	companyState?: string;
	maxVehicleCount?: number;
	selfServiceVehicleChangesRemaining?: number | null;
	hasAdditionalInfo: boolean;
};
export const VehicleManagementSection = ({
	vehicles,
	permitStatus,
	publicAccessToken,
	parkerId,
	isEditPermitDisabled,
	publicIdentifier,
	companyCountry,
	companyState,
	maxVehicleCount,
	selfServiceVehicleChangesRemaining,
	hasAdditionalInfo,
}: VehicleManagementSectionProps) => {
	const { isKiosk } = useKiosk();
	const { t } = useLingui();
	const [isEditVehicleModalOpen, setIsEditVehicleModalOpen] = useState(false);
	const isActiveOrUpcomingPermit = ["ACTIVE", "UPCOMING"].includes(permitStatus);
	const hasPublicAccessToken = !!publicAccessToken;
	const canEditPermit =
		isActiveOrUpcomingPermit && (hasPublicAccessToken || !!parkerId) && !isEditPermitDisabled && !isKiosk;
	const platesLabelId = `plates-${useId()}`;
	return (
		<>
			<div className="mt-4 mb-2 flex items-center justify-between gap-3 border-neutral-faded border-t pt-6">
				<Heading As="h3" kind="h3">
					{hasAdditionalInfo ? t`Registration Information` : t`Vehicle Information`}
				</Heading>
				{canEditPermit && (
					<Button
						type="button"
						className="print:hidden"
						size="sm"
						intent="tertiary"
						onClick={() => setIsEditVehicleModalOpen(true)}
						aria-label={t({
							message: "Change license plate",
							comment:
								"Accessible name of the Change button in the Vehicle Information header; must start with the same word as the visible Change button label so voice-control users can target it",
						})}
					>
						{t({
							message: "Change",
							comment:
								"Button in the Vehicle Information header of the permit confirmation page; opens the dialog to edit the license plates on the permit",
						})}
					</Button>
				)}
			</div>

			<div className="flex flex-col gap-2">
				<div className="flex flex-col">
					<Text As="span" id={platesLabelId} size="sm" intent="muted" weight="bold">
						{t`Plates`}
					</Text>
					<div className="flex flex-wrap gap-3" aria-labelledby={platesLabelId}>
						{vehicles.map(({ id, state, plate }) => (
							<LicensePlate key={id} state={state} plate={plate} />
						))}
					</div>
				</div>
			</div>

			{canEditPermit && (
				<EditPermitVehiclesModal
					isOpen={isEditVehicleModalOpen}
					onClose={() => setIsEditVehicleModalOpen(false)}
					vehicles={vehicles}
					publicIdentifier={publicIdentifier}
					publicAccessToken={publicAccessToken ?? undefined}
					country={companyCountry}
					companyState={companyState}
					maxVehicleCount={maxVehicleCount}
					selfServiceVehicleChangesRemaining={selfServiceVehicleChangesRemaining}
					isParker={!!parkerId}
				/>
			)}
		</>
	);
};
